// ─────────────────────────────────────────────────────────────────────────────
// Translation loading + cache.
//
// Every namespace is its own `import()`, so Parcel code-splits each module's
// strings into its own asset and a locale is only ever downloaded once someone
// actually selects it. English is not on the wire for an operator who never
// leaves Persian.
//
// Two caches, both process-lifetime:
//   `cache`    locale/namespace → the parsed bundle. A second switch back to a
//              language is synchronous — nothing is re-fetched or re-parsed.
//   `inflight` locale/namespace → the in-progress promise, so N simultaneous
//              requests for the same file share one network round trip.
//
// The map below is written out by hand rather than built from a template
// literal because Parcel resolves `import()` statically: a computed specifier
// would silently produce a bundle with no translations in it.
// ─────────────────────────────────────────────────────────────────────────────

import { NAMESPACES, isSupportedLocale } from './config';

const IMPORTERS = {
  fa: {
    common:     () => import('./translations/fa/common.json'),
    nav:        () => import('./translations/fa/nav.json'),
    validation: () => import('./translations/fa/validation.json'),
    errors:     () => import('./translations/fa/errors.json'),
    auth:       () => import('./translations/fa/auth.json'),
    shortcuts:  () => import('./translations/fa/shortcuts.json'),
    dashboard:  () => import('./translations/fa/dashboard.json'),
    clinics:    () => import('./translations/fa/clinics.json'),
    clinic:     () => import('./translations/fa/clinic.json'),
    aiPricing:  () => import('./translations/fa/aiPricing.json'),
    marketplace: () => import('./translations/fa/marketplace.json'),
    demoRequests: () => import('./translations/fa/demoRequests.json'),
    whatsapp:   () => import('./translations/fa/whatsapp.json'),
    doctorPages: () => import('./translations/fa/doctorPages.json'),
    countries:  () => import('./translations/fa/countries.json'),
    accounts:   () => import('./translations/fa/accounts.json'),
  },
  en: {
    common:     () => import('./translations/en/common.json'),
    nav:        () => import('./translations/en/nav.json'),
    validation: () => import('./translations/en/validation.json'),
    errors:     () => import('./translations/en/errors.json'),
    auth:       () => import('./translations/en/auth.json'),
    shortcuts:  () => import('./translations/en/shortcuts.json'),
    dashboard:  () => import('./translations/en/dashboard.json'),
    clinics:    () => import('./translations/en/clinics.json'),
    clinic:     () => import('./translations/en/clinic.json'),
    aiPricing:  () => import('./translations/en/aiPricing.json'),
    marketplace: () => import('./translations/en/marketplace.json'),
    demoRequests: () => import('./translations/en/demoRequests.json'),
    whatsapp:   () => import('./translations/en/whatsapp.json'),
    doctorPages: () => import('./translations/en/doctorPages.json'),
    countries:  () => import('./translations/en/countries.json'),
    accounts:   () => import('./translations/en/accounts.json'),
  },
  fr: {
    common:     () => import('./translations/fr/common.json'),
    nav:        () => import('./translations/fr/nav.json'),
    validation: () => import('./translations/fr/validation.json'),
    errors:     () => import('./translations/fr/errors.json'),
    auth:       () => import('./translations/fr/auth.json'),
    shortcuts:  () => import('./translations/fr/shortcuts.json'),
    dashboard:  () => import('./translations/fr/dashboard.json'),
    clinics:    () => import('./translations/fr/clinics.json'),
    clinic:     () => import('./translations/fr/clinic.json'),
    aiPricing:  () => import('./translations/fr/aiPricing.json'),
    marketplace: () => import('./translations/fr/marketplace.json'),
    demoRequests: () => import('./translations/fr/demoRequests.json'),
    whatsapp:   () => import('./translations/fr/whatsapp.json'),
    doctorPages: () => import('./translations/fr/doctorPages.json'),
    countries:  () => import('./translations/fr/countries.json'),
    accounts:   () => import('./translations/fr/accounts.json'),
  },
  de: {
    common:     () => import('./translations/de/common.json'),
    nav:        () => import('./translations/de/nav.json'),
    validation: () => import('./translations/de/validation.json'),
    errors:     () => import('./translations/de/errors.json'),
    auth:       () => import('./translations/de/auth.json'),
    shortcuts:  () => import('./translations/de/shortcuts.json'),
    dashboard:  () => import('./translations/de/dashboard.json'),
    clinics:    () => import('./translations/de/clinics.json'),
    clinic:     () => import('./translations/de/clinic.json'),
    aiPricing:  () => import('./translations/de/aiPricing.json'),
    marketplace: () => import('./translations/de/marketplace.json'),
    demoRequests: () => import('./translations/de/demoRequests.json'),
    whatsapp:   () => import('./translations/de/whatsapp.json'),
    doctorPages: () => import('./translations/de/doctorPages.json'),
    countries:  () => import('./translations/de/countries.json'),
    accounts:   () => import('./translations/de/accounts.json'),
  },
};

const cache = new Map();
const inflight = new Map();

const cacheKey = (locale, namespace) => `${locale}/${namespace}`;

// Parcel hands back a module namespace object; the JSON is on `default` for a
// JSON asset, but a plain object comes through untouched under Vitest's transform.
const unwrap = (mod) => (mod && mod.default) || mod || {};

/**
 * One namespace, cached. Never rejects: a namespace that fails to load resolves
 * to `{}` and every key in it falls through to the fallback locale, because a
 * failed download of a translation file must not take the panel with it.
 */
export function loadNamespace(locale, namespace) {
  const key = cacheKey(locale, namespace);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);

  const importer = IMPORTERS[locale]?.[namespace];
  if (!importer) {
    cache.set(key, {});
    return Promise.resolve({});
  }

  const promise = importer()
    .then((mod) => unwrap(mod))
    .catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[i18n] failed to load ${key}:`, err);
      }
      return {};
    })
    .then((bundle) => {
      cache.set(key, bundle);
      inflight.delete(key);
      return bundle;
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Every namespace for a locale, as `{ [namespace]: bundle }`.
 *
 * All namespaces are fetched together rather than per-screen on purpose. The
 * whole catalogue is a few tens of kilobytes and the alternative — a screen
 * painting its keys while its namespace is still in flight — is a visible flash
 * of `clinics.columns.until` on the screen an operator is reading. Per-namespace
 * loading is still available via `loadNamespace` if a future feature is heavy
 * enough to want it; the cache and the key format do not change either way.
 */
export function loadCatalog(locale) {
  if (!isSupportedLocale(locale)) return Promise.resolve({});
  return Promise.all(
    NAMESPACES.map((ns) => loadNamespace(locale, ns).then((bundle) => [ns, bundle])),
  ).then(Object.fromEntries);
}

/**
 * The catalogue for a locale if it is already cached, else null. Lets the
 * provider swap languages synchronously on a second visit — no loading state,
 * no re-render with stale strings.
 */
export function peekCatalog(locale) {
  const entries = NAMESPACES.map((ns) => [ns, cache.get(cacheKey(locale, ns))]);
  return entries.every(([, bundle]) => bundle !== undefined) ? Object.fromEntries(entries) : null;
}

/**
 * Warm a locale without switching to it — the account menu's picker calls this
 * when an option is focused, so choosing it is instant. Fire-and-forget.
 */
export function preloadCatalog(locale) {
  loadCatalog(locale).catch(() => {});
}

/** Test seam: drop every cached bundle. Not used by app code. */
export function __resetCatalogCache() {
  cache.clear();
  inflight.clear();
}
